/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import type { ViewEvent } from 'client-code/livelog-events';

import type { ILivelogEventsService } from '../../interfaces/ILivelogEventsService';
import { Log } from '../Log';

/** Minimum view duration in seconds to be eligible for upload (120s = 2 minutes). */
const MIN_VIEW_DURATION_SECS = 120;

/** Maximum view duration in seconds to be eligible for upload (14400s = 4 hours). */
const MAX_VIEW_DURATION_SECS = 14400;

/** Minimum timer delay in seconds (0 = upload as soon as possible). */
const VIEW_EVENTS_MIN_DELAY_SECS = 0;

/** Maximum timer delay in seconds (300s = 5 minutes, randomized). */
const VIEW_EVENTS_MAX_DELAY_SECS = 300;

/** Milliseconds per second. */
const MS_PER_SEC = 1000;

/**
 * Model for ViewEvents.
 * - Buffers events and uploads on a randomized 0–300 second (0–5 minute) timer.
 * - Filters out events with viewDuration < 120 seconds (2 minutes) or > 14400 seconds (4 hours).
 * - Uses fire-and-forget upload — errors are logged and silently dropped.
 */
export class ViewEventsModel {
    private readonly TAG: string = `[livelogEvent] ${this.constructor.name}`;

    private eventBuffer: ViewEvent[] = [];

    private uploadTimer: ReturnType<typeof setTimeout> | null = null;

    private mLivelogEventsService: ILivelogEventsService;

    public constructor(livelogEventsService: ILivelogEventsService) {
        this.mLivelogEventsService = livelogEventsService;
    }

    /**
     * Adds a ViewEvent to the buffer and starts the upload timer
     * if one is not already running.
     */
    public uploadEvent(event: ViewEvent): void {
        this.eventBuffer.push(event);

        if (!this.uploadTimer) {
            this.startTimer();
        }
    }

    /**
     * Starts the upload timer with a randomized delay between
     * VIEW_EVENTS_MIN_DELAY_SECS and VIEW_EVENTS_MAX_DELAY_SECS.
     */
    private startTimer(): void {
        const minMs = VIEW_EVENTS_MIN_DELAY_SECS * MS_PER_SEC;
        const maxMs = VIEW_EVENTS_MAX_DELAY_SECS * MS_PER_SEC;
        const delay = minMs + Math.random() * (maxMs - minMs);

        Log.i(this.TAG, `Upload timer started, delay: ${Math.round(delay)}ms`);

        this.uploadTimer = setTimeout(() => {
            this.flushEvents();
        }, delay);
    }

    /**
     * Stops the timer and uploads all buffered events.
     */
    private flushEvents(): void {
        if (this.uploadTimer) {
            clearTimeout(this.uploadTimer);
            this.uploadTimer = null;
        }

        if (this.eventBuffer.length === 0) {
            return;
        }

        const eventsToUpload = [...this.eventBuffer];
        this.eventBuffer = [];

        this.uploadViewEvents(eventsToUpload);
    }

    /**
     * Filters events by minimum view duration and uploads via the service.
     * Uses fire-and-forget — errors are logged and silently dropped.
     */
    private uploadViewEvents(events: ViewEvent[]): void {
        const viewEvents = events.filter((event) => {
            if (event.viewDuration !== undefined && event.viewDuration < MIN_VIEW_DURATION_SECS) {
                Log.i(
                    this.TAG,
                    `Dropping ViewEvent (viewDuration=${event.viewDuration}s` +
                        ` < ${MIN_VIEW_DURATION_SECS}s)`,
                );
                return false;
            }
            if (event.viewDuration !== undefined && event.viewDuration > MAX_VIEW_DURATION_SECS) {
                Log.i(
                    this.TAG,
                    `Dropping ViewEvent (viewDuration=${event.viewDuration}s` +
                        ` > ${MAX_VIEW_DURATION_SECS}s)`,
                );
                return false;
            }
            return true;
        });

        if (viewEvents.length === 0) {
            Log.i(this.TAG, 'No eligible ViewEvents to upload after filtering.');
            return;
        }

        Log.i(this.TAG, `Uploading ${viewEvents.length} ViewEvents (fire-and-forget).`);
        this.mLivelogEventsService.postViewEvents(viewEvents).catch((error) => {
            Log.e(this.TAG, 'Failed to upload ViewEvents (silently dropped).', error);
        });
    }
}
